import type { GencowCtx } from "./context.js";
import {
  buildRealtimeQueryScopeHash,
  buildRealtimeQuerySubscriptionKey,
  type RealtimeSubscriptionPolicy,
} from "./realtime-query-subscription.js";
import type { InferArgs } from "./v.js";

export type RealtimeQueryAuthorizeResult =
  | {
      subscriptionKey?: string;
      scope?: unknown;
      ttlSeconds?: number;
      epoch?: string | number;
    }
  | string
  | false
  | null
  | undefined
  | void;

export type RealtimeQueryAuthorizeHandler<TSchema = unknown> = (
  ctx: GencowCtx,
  args: InferArgs<TSchema>,
) => RealtimeQueryAuthorizeResult | Promise<RealtimeQueryAuthorizeResult>;

export type RealtimeQueryKeyBuilder<TSchema = unknown> = (args: InferArgs<TSchema>) => string;
export type RealtimeQueryScopeBuilder<TSchema = unknown> = (args: InferArgs<TSchema>) => unknown;

export interface RealtimeQuerySubscriptionDef<TSchema = unknown> {
  name: string;
  policy: RealtimeSubscriptionPolicy;
  argsSchema?: TSchema;
  subscriptionKey?: RealtimeQueryKeyBuilder<TSchema>;
  scope?: RealtimeQueryScopeBuilder<TSchema>;
  authorize?: RealtimeQueryAuthorizeHandler<TSchema>;
}

export type RealtimeQuerySubscriptionInput<TSchema = unknown> = {
  policy: RealtimeSubscriptionPolicy;
  args?: TSchema;
  subscriptionKey?: RealtimeQueryKeyBuilder<TSchema>;
  scope?: RealtimeQueryScopeBuilder<TSchema>;
  authorize?: RealtimeQueryAuthorizeHandler<TSchema>;
};

declare global {
  var __gencow_realtimeQuerySubscriptionRegistry: Map<string, RealtimeQuerySubscriptionDef>;
}

if (!globalThis.__gencow_realtimeQuerySubscriptionRegistry) {
  globalThis.__gencow_realtimeQuerySubscriptionRegistry = new Map();
}

export const realtimeQuerySubscriptionRegistry = globalThis.__gencow_realtimeQuerySubscriptionRegistry;

export function defineRealtimeSubscriptions<
  const TSubscriptions extends Record<string, RealtimeQuerySubscriptionInput>,
>(subscriptions: TSubscriptions): TSubscriptions {
  for (const [name, def] of Object.entries(subscriptions)) {
    realtimeQuerySubscriptionRegistry.set(name, {
      name,
      policy: def.policy,
      argsSchema: def.args,
      subscriptionKey: def.subscriptionKey as RealtimeQueryKeyBuilder | undefined,
      scope: def.scope as RealtimeQueryScopeBuilder | undefined,
      authorize: def.authorize as RealtimeQueryAuthorizeHandler | undefined,
    });
  }
  return subscriptions;
}

export function getRealtimeSubscriptionDef(name: string): RealtimeQuerySubscriptionDef | undefined {
  return realtimeQuerySubscriptionRegistry.get(name);
}

export function getRegisteredRealtimeSubscriptions(): string[] {
  return [...realtimeQuerySubscriptionRegistry.keys()];
}

export function resolveRealtimeQuerySubscriptionKey(params: {
  query: string;
  args?: unknown;
  result?: RealtimeQueryAuthorizeResult;
}): string | null {
  if (params.result === false || params.result === null) return null;
  if (typeof params.result === "string") return params.result.length > 0 ? params.result : null;
  if (params.result && typeof params.result === "object" && "subscriptionKey" in params.result) {
    const key = params.result.subscriptionKey;
    if (key !== undefined) return typeof key === "string" && key.length > 0 ? key : null;
  }
  const def = getRealtimeSubscriptionDef(params.query);
  const subscriptionKey = def?.subscriptionKey?.(params.args as never);
  if (subscriptionKey !== undefined) {
    return typeof subscriptionKey === "string" && subscriptionKey.length > 0 ? subscriptionKey : null;
  }
  return buildRealtimeQuerySubscriptionKey(params.query, params.args);
}

export function resolveRealtimeQueryScopeHash(params: {
  query: string;
  args?: unknown;
  subscriptionKey: string;
  result?: RealtimeQueryAuthorizeResult;
}): string {
  const objectResult =
    params.result && typeof params.result === "object" && !Array.isArray(params.result)
      ? params.result
      : null;
  const resultScope = objectResult && "scope" in objectResult ? objectResult.scope : undefined;
  const def = getRealtimeSubscriptionDef(params.query);
  const scope = resultScope !== undefined ? resultScope : def?.scope?.(params.args as never);
  return buildRealtimeQueryScopeHash({
    query: params.query,
    subscriptionKey: params.subscriptionKey,
    scope,
  });
}

export function resetRealtimeSubscriptionRegistryForTests(): void {
  realtimeQuerySubscriptionRegistry.clear();
}
